import { formatUnits } from 'viem'

/**
 * "Earn" on the profile (Bez, 2026-10-09): the affiliate ("shiller") programme of the V4 competition contract, in plain words.
 * Pure parts, tested (logic.test.ts). Contract facts (CadinuTradingCompetitionV4, 0xce96…40CB):
 *  - the referrer's commission is a share of the creation fee the project actually paid, in the currency it paid in;
 *  - the share depends on the referrer's count of QUALIFYING creations in that currency: 1-10 -> referralTierRates[0],
 *    11-100 -> [1], 101-500 -> [2], 501+ -> [3]; the thresholds are fixed in code, the rates are owner-editable (max 6000 bps);
 *  - a creation counts toward the tier only when its reward token is listed with a configured minimum and the reward meets it;
 *    the commission is credited either way;
 *  - an owner override fixes one referrer's rate (getNextReferralRate returns it);
 *  - credit accrues in referralCredit[currency][referrer] and only the referrer withdraws it (withdrawReferralCredit(currency)).
 */

export type Cur = 'bnb' | 'cbon'
export const CURS: Cur[] = ['bnb', 'cbon']
export const CUR_INDEX: Record<Cur, 0 | 1> = { bnb: 0, cbon: 1 }
export const SYMBOL: Record<Cur, string> = { bnb: 'BNB', cbon: 'CBON' }
/** last qualifying creation of tiers 0, 1 and 2 (fixed in the contract) */
export const TIER_BOUNDS = [10, 100, 500] as const
export const MAX_REFERRAL_BPS = 6000
/** what the page shows for a figure it does not have (never an invented one) */
export const NONE = '—'
const ADDR = /^0x[0-9a-fA-F]{40}$/

/* ───────────────────────── config ───────────────────────── */

export interface FeePair {
  list: bigint
  withRef: bigint
  enabled: boolean
}
export interface EarnConfig {
  fees: Record<Cur, FeePair | null>
  /** the contract's referralTierRates as the API read them; null until the API has read them (no fallback: Bez 2026-10-09) */
  tiersBps: number[] | null
}

const big = (v: unknown): bigint | null => {
  try {
    return typeof v === 'string' && /^\d+$/.test(v) ? BigInt(v) : null
  } catch {
    return null
  }
}
const validTiers = (t: unknown): t is number[] =>
  Array.isArray(t) && t.length === 4 && t.every((x, i) => Number.isInteger(x) && x >= 0 && x <= MAX_REFERRAL_BPS && (i === 0 || x >= (t[i - 1] as number)))

/** The public V4 config (GET /api/competitions/v4/config) as the page uses it; anything malformed is null, never a guessed fee. */
export function parseConfig(raw: unknown): EarnConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as { fees?: Record<string, { creationWei?: unknown; withReferralWei?: unknown; enabled?: unknown }> | null; referralTiersBps?: unknown }
  const fee = (c: Cur): FeePair | null => {
    const f = r.fees?.[c]
    const list = big(f?.creationWei)
    const withRef = big(f?.withReferralWei)
    return list !== null && withRef !== null && withRef <= list ? { list, withRef, enabled: f?.enabled !== false } : null
  }
  return { fees: { bnb: fee('bnb'), cbon: fee('cbon') }, tiersBps: validTiers(r.referralTiersBps) ? r.referralTiersBps : null }
}

/** The referral price as a discount on the list price, in bps (0.04 instead of 0.05 = 2000); null without a fee. */
export const discountBps = (f: FeePair | null | undefined): number | null => (f && f.list > 0n ? Number(((f.list - f.withRef) * 10000n) / f.list) : null)

/* ───────────────────────── tiers and progress ───────────────────────── */

/** The tier of the n-th qualifying creation (1-based) in one currency. */
export function tierIndexFor(n: number): 0 | 1 | 2 | 3 {
  if (n <= TIER_BOUNDS[0]) return 0
  if (n <= TIER_BOUNDS[1]) return 1
  if (n <= TIER_BOUNDS[2]) return 2
  return 3
}

const ordinal = (n: number) => {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'
  return `${n}${s}`
}

export interface TierRow {
  index: 0 | 1 | 2 | 3
  from: number
  to: number | null
  /** null while the rates are not known */
  bps: number | null
  /** "1st to 10th project", "501st project and up" (the bounds are fixed in the contract) */
  range: string
}
export function tierRows(bps: number[] | null): TierRow[] {
  return [0, 1, 2, 3].map((i) => {
    const from = i === 0 ? 1 : TIER_BOUNDS[i - 1] + 1
    const to = i < 3 ? TIER_BOUNDS[i] : null
    return { index: i as TierRow['index'], from, to, bps: bps ? bps[i] : null, range: to ? `${ordinal(from)} to ${ordinal(to)} project` : `${ordinal(from)} project and up` }
  })
}

/** "20 %", "22.5 %" (Bez writes the space); "—" when unknown. */
export const pct = (bps: number | null | undefined) => (bps === null || bps === undefined || !Number.isFinite(bps) ? NONE : `${Number((bps / 100).toFixed(2))} %`)

export interface Progress {
  /** qualifying creations so far in this currency */
  count: number
  /** the tier the NEXT creation falls in */
  tier: 0 | 1 | 2 | 3
  /** the rate of the next creation (the override when one is set); null while the rates are not known */
  rateBps: number | null
  /** the next tier's rate; null at the top tier, with an override, or while the rates are not known */
  nextBps: number | null
  /** qualifying creations still needed before a creation earns the next rate; null at the top or with an override */
  toNext: number | null
  /** how far through the current tier, 0 to 1 */
  inTier: number
  custom: boolean
}
export function progressFor(count: number, bps: number[] | null, override?: { set: boolean; bps: number } | null): Progress {
  const c = Math.max(0, Math.floor(count))
  const tier = tierIndexFor(c + 1)
  const custom = Boolean(override?.set)
  const rateBps = custom && override ? override.bps : bps ? bps[tier] : null
  if (custom || tier === 3) return { count: c, tier, rateBps, nextBps: null, toNext: null, inTier: 1, custom }
  const prev = tier === 0 ? 0 : TIER_BOUNDS[tier - 1]
  const bound = TIER_BOUNDS[tier]
  return { count: c, tier, rateBps, nextBps: bps ? bps[tier + 1] : null, toNext: bound - c, inTier: (c - prev) / (bound - prev), custom }
}

/** The progress line under the bar. */
export function progressText(p: Progress): string {
  if (p.custom) return `A fixed rate of ${pct(p.rateBps)} is set for your wallet.`
  if (p.toNext === null) return p.rateBps === null ? 'Top tier.' : `Top rate: ${pct(p.rateBps)} on every project.`
  const more = `${p.toNext} more qualifying ${p.toNext === 1 ? 'project' : 'projects'}`
  return p.nextBps === null ? `${more} to the next tier.` : `${more} to reach ${pct(p.nextBps)}.`
}

/** The contract's split: fee * rate / 10000, rounded down. */
export const commission = (fee: bigint, bps: number) => (fee * BigInt(Math.round(bps))) / 10000n

/* ───────────────────────── amounts ───────────────────────── */

const group = (int: string) => int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

/** Every digit, grouped: what the wallet will receive ("0.017", "34,000,000"). */
export function fmtExact(wei: bigint, decimals = 18): string {
  const [i, f] = formatUnits(wei, decimals).split('.')
  return f ? `${group(i)}.${f}` : group(i)
}

const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 })
/** "0.017 BNB", "34M CBON", "0 BNB". BNB keeps up to 6 decimals (cut, never rounded up); CBON is compact. */
export function fmtAmount(wei: bigint, cur: Cur): string {
  if (cur === 'cbon') {
    const n = Number(formatUnits(wei, 18))
    return `${n >= 1000 ? COMPACT.format(n) : Number(n.toFixed(2)).toLocaleString('en-US')} CBON`
  }
  const [i, f = ''] = formatUnits(wei, 18).split('.')
  const frac = f.slice(0, 6).replace(/0+$/, '')
  if (wei > 0n && i === '0' && !frac) return '<0.000001 BNB'
  return `${group(i)}${frac ? `.${frac}` : ''} BNB`
}

/** "$13", "$4.20", "<$0.01"; null when the price is unknown (never "$0" for an unknown price). */
export function fmtUsd(n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return null
  if (n === 0) return '$0'
  if (n < 0.01) return '<$0.01'
  if (n < 10) return `$${n.toFixed(2)}`
  return `$${Math.round(n).toLocaleString('en-US')}`
}
export const usdOf = (wei: bigint, priceUsd: number | null | undefined) =>
  priceUsd && Number.isFinite(priceUsd) && priceUsd > 0 ? Number(formatUnits(wei, 18)) * priceUsd : null

export interface Example {
  fee: string
  share: string
  usd: string | null
  shareWei: bigint
}
/** One project creating with the link: what it pays (the referral price) and what the referrer gets at `bps`. */
export function example(fees: FeePair, cur: Cur, bps: number, priceUsd?: number | null): Example {
  const shareWei = commission(fees.withRef, bps)
  return { fee: fmtAmount(fees.withRef, cur), share: fmtAmount(shareWei, cur), usd: fmtUsd(usdOf(shareWei, priceUsd)), shareWei }
}

/**
 * "Each project that creates a competition with your link pays 0.04 BNB (20 % off); you get 30 % = 0.012 BNB ≈ $9.12 now."
 * Without the rates: the price only, and that the share is loading (never a guessed rate).
 */
export function leadSentence(fees: FeePair, cur: Cur, bps: number | null, priceUsd?: number | null): string {
  const d = discountBps(fees)
  const pays = `Each project that creates a competition with your link pays ${fmtAmount(fees.withRef, cur)}${d ? ` (${pct(d)} off)` : ''}`
  if (bps === null) return `${pays}; your share of it is loading.`
  const e = example(fees, cur, bps, priceUsd)
  return `${pays}; you get ${pct(bps)} = ${e.share}${e.usd ? ` ≈ ${e.usd} now` : ''}.`
}

/* ───────────────────────── shiller kit ───────────────────────── */

/**
 * Words a kit text never uses: no promise of safety, results or price (Bez, 2026-10-09: honest claims only, no "guaranteed",
 * no "audited", no price promises). Checked by the tests on every text the kit can produce.
 */
export const BANNED: RegExp[] = [
  /guarant/i,
  /audit/i,
  /risk[\s-]?free/i,
  /no risk/i,
  /\bsafe\b/i,
  /\b\d+\s?x\b/i,
  /moon/i,
  /pump/i,
  /price will/i,
  /profit/i,
  /\breturns?\b/i,
  /passive income/i,
  /get rich/i,
  /financial advice/i,
]
export const bannedIn = (text: string) => BANNED.filter((re) => re.test(text)).map((re) => re.source)

export type KitId = 'x' | 'telegram' | 'dm'
export interface KitText {
  id: KitId
  label: string
  text: string
}
/**
 * Three ready-to-copy texts with the link. Only facts the contract and the create page make true: any token with a pool on the
 * 8 listed exchanges, the creator sets pool / prize / winners, the prize is deposited in the contract at creation and the
 * winners claim it on BNB Chain, the referral price is lower than the list price.
 */
export function kitTexts(link: string, fees: Partial<Record<Cur, FeePair | null>>): KitText[] {
  const { bnb } = fees
  const cbon = fees.cbon?.enabled ? fees.cbon : null
  const off = discountBps(bnb)
  const price = bnb ? `${fmtAmount(bnb.withRef, 'bnb')} instead of ${fmtAmount(bnb.list, 'bnb')}${off ? ` (${pct(off)} off)` : ''}` : 'the referral price'
  const both = cbon ? `${price}, or ${fmtAmount(cbon.withRef, 'cbon')} instead of ${fmtAmount(cbon.list, 'cbon')}` : price
  return [
    {
      id: 'x',
      label: 'Post on X',
      text: `Token on BNB Chain? Run a trading competition for it on CADINU: you pick the pool and the prize, the top traders share it and claim it on-chain. Created with my link, the fee is ${price}. ${link}`,
    },
    {
      id: 'telegram',
      label: 'Telegram group',
      text: `For projects on BNB Chain: CADINU runs trading competitions for any token with a pool on one of 8 listed exchanges. You choose the pool, the prize and the number of winners; the prize is deposited in the contract when you create it, and the winners claim it on-chain. Through my link the creation fee is ${both}.\n${link}`,
    },
    {
      id: 'dm',
      label: 'DM to a project',
      text: `Hi! Have you thought about a trading competition for your token? On CADINU you set the pool, the prize and the number of winners, and the top traders share the prize when it ends; the prize sits in the contract until the winners claim it on BNB Chain. If you create it with my link, the fee is ${price}. Happy to walk you through it: ${link}`,
    },
  ]
}

/** X counts every link as 23 characters. */
export const xLength = (text: string) => text.replace(/https?:\/\/\S+/g, 'x'.repeat(23)).length

/* ───────────────────────── board and history (from the V4 competitions list) ───────────────────────── */

export interface CompRow {
  id: number
  startTime?: number
  createdTx?: string | null
  projectToken?: string
  fee?: { currency?: string; feePaid?: string; referrer?: string | null; commission?: string } | null
}
export interface BoardEntry {
  address: string
  /** competitions created with this wallet's link that paid it a commission */
  count: number
  bnbWei: string
  cbonWei: string
}
export interface HistoryItem {
  id: number
  currency: Cur
  commissionWei: string
  feePaidWei: string
  startTime: number | null
  tx: string | null
}

const credited = (r: CompRow): { referrer: string; cur: Cur; commission: bigint; fee: bigint } | null => {
  const f = r?.fee
  const ref = typeof f?.referrer === 'string' ? f.referrer.toLowerCase() : ''
  const cur = f?.currency === 'bnb' || f?.currency === 'cbon' ? f.currency : null
  const c = big(f?.commission)
  if (!ADDR.test(ref) || !cur || c === null || c === 0n) return null
  return { referrer: ref, cur, commission: c, fee: big(f?.feePaid) ?? 0n }
}

/** The top affiliates by projects brought, then BNB earned, then CBON earned. Only public facts: address, count, amounts. */
export function aggregateBoard(rows: CompRow[], limit = 10): { top: BoardEntry[]; affiliates: number } {
  const by = new Map<string, { count: number; bnb: bigint; cbon: bigint }>()
  for (const r of rows) {
    const c = credited(r)
    if (c) {
      const e = by.get(c.referrer) ?? { count: 0, bnb: 0n, cbon: 0n }
      e.count += 1
      e[c.cur] += c.commission
      by.set(c.referrer, e)
    }
  }
  const all = Array.from(by.entries()).sort(
    ([a, x], [b, y]) => y.count - x.count || (y.bnb > x.bnb ? 1 : y.bnb < x.bnb ? -1 : 0) || (y.cbon > x.cbon ? 1 : y.cbon < x.cbon ? -1 : 0) || (a < b ? -1 : 1),
  )
  return { top: all.slice(0, limit).map(([address, e]) => ({ address, count: e.count, bnbWei: e.bnb.toString(), cbonWei: e.cbon.toString() })), affiliates: all.length }
}

/** The competitions one wallet's link brought, newest first. */
export function historyFor(rows: CompRow[], wallet: string, limit = 20): HistoryItem[] {
  const w = wallet.toLowerCase()
  return rows
    .map((r) => ({ r, c: credited(r) }))
    .filter(({ c }) => c?.referrer === w)
    .sort((a, b) => b.r.id - a.r.id)
    .slice(0, limit)
    .map(({ r, c }) => ({
      id: r.id,
      currency: (c as NonNullable<typeof c>).cur,
      commissionWei: (c as NonNullable<typeof c>).commission.toString(),
      feePaidWei: (c as NonNullable<typeof c>).fee.toString(),
      startTime: typeof r.startTime === 'number' ? r.startTime : null,
      tx: typeof r.createdTx === 'string' ? r.createdTx : null,
    }))
}

/** "0.034 BNB · 34M CBON" (a zero currency is left out; both zero: "0 BNB"). */
export function earnedText(bnbWei: string | bigint, cbonWei: string | bigint): string {
  const b = BigInt(bnbWei)
  const c = BigInt(cbonWei)
  const parts = [b > 0n ? fmtAmount(b, 'bnb') : null, c > 0n ? fmtAmount(c, 'cbon') : null].filter(Boolean)
  return parts.length ? parts.join(' · ') : '0 BNB'
}

/* ───────────────────────── share card ───────────────────────── */

/** A referral id the card route accepts: a wallet, or a handle (3-15 letters, digits, "." and "_", starting with a letter). */
export const REF_ID_RE = /^(0x[0-9a-fA-F]{40}|[A-Za-z][A-Za-z0-9._]{2,14})$/
export const earnCardPath = (id: string) => `/api/earn/card/${encodeURIComponent(id)}.png`

/** The Earn section of the profile (the competitions' "Rewards & referral" tab links here). */
export const EARN_PATH = '/u#earn'
